/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {Control} from "@luciad/ria-sample-common/control/Control";
import {SliderComponent} from "@luciad/ria-sample-common/slider/SliderComponent";

export class ThemedPanel extends Control {

  constructor(map, options, themes) {
    options = options || {};
    options.title = options.title || "ThemedPanel";
    options.icon = options.icon || "lcdIconFolder";
    super(map, options);

    this._themes = themes;
    this._themeNodes = {
      themeButtons: {},
      themeContainers: {},
      onThemeSelect: {},
      themeActiveControls: {}
    };
  }

  init(map, options) {
    super.init(map, options);
    this._panelNode.classList.add("ruler-container");

    this.createThemeButtons();
    this.createThemeContainers(map);
    this.changeTheme(null);
  }

  createThemeContainers() {
    const containers = this._themeNodes.themeContainers;
    const themeButtons = this._themeNodes.themeButtons;
    const themeActiveControls = this._themeNodes.themeActiveControls;
    const onThemeSelect = this._themeNodes.onThemeSelect;

    this._themes.forEach(themeItem => {
      const container = document.createElement('div');
      container.setAttribute('themeId', "" + themeItem.id);
      container.classList.add("panelInfo");
      const themeControl = createThemeControl(container);
      containers[themeItem.id] = container;
      const onChange = themeItem.init(container, themeControl, this._map);
      onThemeSelect[themeItem.id] = onChange;
      themeControl.addThemeActiveLister(() => {
        onChange();
        setStyleClass(themeButtons[themeItem.id], "emphasize", themeControl.isActive());
      });
      themeActiveControls[themeItem.id] = themeControl;
      this._panelNode.appendChild(container);
    });
  }

  createThemeButtons() {
    const is3DMap = this._map.is3D();
    const themeButtons = this._themeNodes.themeButtons;

    this._toggleThemeButtonGroup = document.createElement('div');
    this._toggleThemeButtonGroup.classList.add("panelButtonGroup");

    this._themes.forEach(themeItem => {
      const themeId = themeItem.id;
      const btn = document.createElement('button');
      btn.innerHTML = themeItem.text;
      btn.classList.add("btn", "panelButton");
      if (themeItem.only3D && !is3DMap) {
        btn.disabled = true;
      }

      // hook click event handler
      btn.addEventListener("click", () => {
        this.changeTheme(themeId);
      }, false);

      themeButtons[themeId] = btn;
      this._toggleThemeButtonGroup.appendChild(btn);
    });
    this._panelNode.appendChild(this._toggleThemeButtonGroup);
  }

  changeTheme(themeId) {
    this._themes.forEach((themeItem) => {
      const id = themeItem.id;

      // highlight the active measure type button
      const btn = this._themeNodes.themeButtons[id];
      setStyleClass(btn, "active", id === themeId);

      // show relevant panel
      const panel = this._themeNodes.themeContainers[id];
      setStyleClass(panel, "hidden", id !== themeId);
    });

    if (themeId) {
      this._themeNodes.onThemeSelect[themeId]();
    }
  }
}

export function addText(parent, label) {
  const nodeText = document.createElement('span');
  nodeText.style.padding = '5px';
  nodeText.innerHTML = label;
  parent.appendChild(nodeText);
}

export function addSlider(parent, sliderOptions) {
  const domSlider = document.createElement('div');
  domSlider.classList.add("form-inline");
  parent.appendChild(domSlider);
  const sliderComponent = new SliderComponent(domSlider, sliderOptions);
  sliderComponent.container.classList.add("effectSlider");
  return sliderComponent;
}

export function createCheckbox(container, label, checkedState) {
  const checkboxNode = document.createElement('input');
  checkboxNode.setAttribute("type", "checkbox");
  checkboxNode.checked = !!checkedState;
  container.appendChild(checkboxNode);
  addText(container, label);
  return checkboxNode;
}

export function setStyleClass(node, className, isActive) {
  if (isActive) {
    node.classList.add(className);
  } else {
    node.classList.remove(className);
  }
}

export function createThemeControl(container) {
  let controllerState = false;
  const node = document.createElement("div");
  node.classList.add("panelInfoColumn");

  const btn = document.createElement('button');
  btn.classList.add("btn", "panelButton");
  btn.innerHTML = getButtonDescription(controllerState);

  const themeActiveChangeListeners = [];

  function toggleState() {
    controllerState = !controllerState;
    btn.innerHTML = getButtonDescription(controllerState);
    for (const listener of themeActiveChangeListeners) {
      listener();
    }
  }
  btn.addEventListener("click", toggleState);

  node.appendChild(btn);
  container.appendChild(node);

  return {
    isActive: function() {
      return controllerState;
    },
    reset: function() {
      if (controllerState) {
        toggleState();
      }
    },
    addThemeActiveLister: (listener) => {
      themeActiveChangeListeners.push(listener);
    }
  };
}

export function getButtonDescription(state) {
  return (!state ? "ZAPNOUT" : "VYPNOUT") + " efekt";
}

export function paramExists(numParam) {
  return typeof numParam === "number";
}